/* scarce-mono dreaming field — WebGL storm behind the reading surface.
 *
 * Slow hazy swirl with meandering vortices (not a bright aurora blotch).
 * Canvas is data-turbo-permanent so Turbo Drive keeps the same WebGL context
 * across visits. Shader phase is wall-clock so a cold remount continues.
 * ~¾-res buffer + style 100% stretch. Ordered dither softens banding.
 * Bottom-left HUD: fps + shader-exec ms, EMAs over 200 samples, every frame,
 * on every page that mounts this pack.
 */

const WORLD_SCALE = 1.15 / 900; // zoomed out — larger field, finer detail
const EMA_N = 200;

function shaderSecs() {
    return (Date.now() * 0.001) % 86400;
}

function makeEma() {
    return { n: 0, sum: 0, ema: 0 };
}

function pushEma(s, x) {
    s.n += 1;
    if (s.n <= EMA_N) {
        s.sum += x;
        s.ema = s.sum / s.n;
    } else {
        s.ema += (x - s.ema) / EMA_N;
    }
    return s.ema;
}

function fmtFps(x) {
    // Always xxx.y (leading zero ok) so the string width doesn't jump at 100.
    const n = Number.isFinite(x) ? Math.max(0, x) : 0;
    const [whole, frac] = n.toFixed(1).split(".");
    return whole.padStart(3, "0") + "." + frac;
}

function fmtMs(x) {
    // xx.y — timer resolution is ~0.1ms in Chromium
    return (Number.isFinite(x) ? x : 0).toFixed(1);
}

function mountDreambg(win, cv) {
    const doc = win.document;
    const gl = cv.getContext("webgl", { antialias: false, depth: false, alpha: false, preserveDrawingBuffer: false });
    const rm = win.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!gl) {
        cv.style.display = "none";
        return null;
    }

    const VS = "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";

    // Slow dreamy storm: curl-warped fbm mist + meandering vortices + dither.
    // Kept dark — soft luminance lift over --sm-bg, never a bright wash.
    const FS = [
        "precision highp float;",
        "uniform float t; uniform vec2 r;",
        "uniform vec2 domainOffset; uniform float domScale;",
        "uniform float warp; uniform float pageTint;",
        "float hash(vec2 p){ p=fract(p*vec2(123.34,345.45));",
        "  p+=dot(p,p+34.345); return fract(p.x*p.y); }",
        "float noise(vec2 p){ vec2 i=floor(p),f=fract(p);",
        "  vec2 u=f*f*(3.0-2.0*f);",
        "  return mix(mix(hash(i),hash(i+vec2(1,0)),u.x),",
        "             mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x),u.y); }",
        "float fbm(vec2 p){ float s=0.0,a=0.5;",
        "  mat2 m=mat2(1.6,1.2,-1.2,1.6);",
        "  for(int i=0;i<5;i++){ s+=a*noise(p); p=m*p; a*=0.55; } return s; }",
        "vec2 rot(vec2 v,float a){ float c=cos(a),s=sin(a); return mat2(c,-s,s,c)*v; }",
        "float vortex(vec2 p,vec2 c,float spin,float rad){",
        "  vec2 d=p-c; float l=length(d)+1e-4;",
        "  float fall=exp(-l*l/(rad*rad));",
        "  vec2 whir=rot(d/l, spin*fall)*l;",
        "  return fbm(whir*1.8+c*0.3)*fall;",
        "}",
        "void main(){",
        "  vec2 uv=gl_FragCoord.xy/r;",
        "  vec2 p=gl_FragCoord.xy*domScale + domainOffset;",
        "  float tt=t*0.011;",
        "  float tt2=t*0.007;",
        /* lower spatial freq = zoomed-out storm */
        "  vec2 q=vec2(fbm(p*0.14+vec2(0.0,tt)), fbm(p*0.14+vec2(4.1,1.7)-tt2));",
        "  vec2 curl=vec2(q.y-0.5, 0.5-q.x);",
        "  p+=curl*(1.4+0.6*warp);",
        "  vec2 c1=vec2(sin(tt2*0.9)*3.2+0.6, cos(tt*0.7)*2.4-0.4);",
        "  vec2 c2=vec2(cos(tt*0.55)*3.6-1.0, sin(tt2*1.1)*2.8+0.8);",
        "  vec2 c3=vec2(sin(tt*0.4+1.7)*4.0, cos(tt2*0.5+0.8)*3.2);",
        "  float v=0.0;",
        "  v+=vortex(p, c1, 2.2+tt*0.12, 2.4);",
        "  v+=vortex(p, c2,-1.7-tt2*0.10, 2.8)*0.85;",
        "  v+=vortex(p, c3, 1.1+tt*0.07, 3.4)*0.55;",
        "  float mist=fbm(p*0.11+vec2(tt*0.4, -tt2*0.35));",
        "  float haze=fbm(p*0.055+q*0.8+tt2*0.2);",
        "  float dens=clamp(mist*0.5 + haze*0.4 + v*0.75, 0.0, 1.0);",
        /* steeper transfer = darker voids, punchier ridges */
        "  dens=smoothstep(0.34, 0.72, dens);",
        "  float soft=pow(dens, 1.9);",
        "  float edge=smoothstep(1.7, 0.25, length(uv-vec2(0.5,0.48)));",
        "  soft*=0.35+0.65*edge;",
        "  vec3 base=vec3(0.043,0.059,0.098);",
        "  vec3 storm=vec3(0.09,0.12,0.24);",
        "  vec3 ink=vec3(0.05,0.07,0.12);",
        "  vec3 lift=vec3(0.18,0.22,0.40);",
        "  vec3 tint=mix(ink, storm, clamp(q.x*0.7+0.3,0.0,1.0));",
        "  tint=mix(tint, lift, soft*0.7);",
        "  vec3 warm=vec3(0.20,0.10,0.18);",
        "  vec3 cool=vec3(0.08,0.13,0.24);",
        "  tint=mix(tint, pageTint>=0.0?warm:cool, clamp(abs(pageTint),0.0,1.0)*0.2);",
        "  float strength=0.13 + 0.05*warp;",
        "  float ambient=0.008;",
        "  vec3 col=base + tint*(soft*strength + ambient);",
        "  col+=storm*(v*0.035);",
        "  float bayer=fract(dot(floor(gl_FragCoord.xy), vec2(0.754877,0.569840)));",
        "  float dither=(bayer + hash(gl_FragCoord.xy+floor(t*3.0)) - 1.0)*(1.0/255.0)*1.8;",
        "  col+=dither;",
        "  gl_FragColor=vec4(col,1.0);",
        "}",
    ].join("\n");

    function compile(type, src) {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
            console.warn("sm-dreambg shader:", gl.getShaderInfoLog(s));
            return null;
        }
        return s;
    }
    function program(fsSrc) {
        const vs = compile(gl.VERTEX_SHADER, VS);
        const fs = compile(gl.FRAGMENT_SHADER, fsSrc);
        if (!vs || !fs) return null;
        const pr = gl.createProgram();
        gl.attachShader(pr, vs);
        gl.attachShader(pr, fs);
        gl.linkProgram(pr);
        if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) {
            console.warn("sm-dreambg link:", gl.getProgramInfoLog(pr));
            return null;
        }
        return pr;
    }

    let prog = program(FS);
    if (!prog) {
        cv.style.display = "none";
        return null;
    }
    const u = {
        t: gl.getUniformLocation(prog, "t"),
        r: gl.getUniformLocation(prog, "r"),
        domainOffset: gl.getUniformLocation(prog, "domainOffset"),
        domScale: gl.getUniformLocation(prog, "domScale"),
        warp: gl.getUniformLocation(prog, "warp"),
        pageTint: gl.getUniformLocation(prog, "pageTint"),
    };
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    let canW = 2, canH = 2;
    function size() {
        canW = Math.max(2, Math.floor(win.innerWidth * 0.75));
        canH = Math.max(2, Math.floor(win.innerHeight * 0.75));
        cv.width = canW;
        cv.height = canH;
        cv.style.width = "100%";
        cv.style.height = "100%";
        cv.style.display = "";
    }
    size();

    let lastMs = 0, lastDrawMs = 0, frameCount = 0;
    let tintCur = 0, tintTarget = 0;
    let warpStart = -1e9, lastWarp = 0;

    let hud = doc.getElementById("sm-dreambg-hud");
    if (!hud) {
        hud = doc.createElement("div");
        hud.id = "sm-dreambg-hud";
        hud.setAttribute("aria-hidden", "true");
    }
    const fpsLine = doc.createElement("div");
    const ftLine = doc.createElement("div");

    const fpsEma = makeEma();
    const ftEma = makeEma();
    let prevFrameMs = 0;
    let lastFps = 0;
    let lastFt = 0;

    function ensureHud() {
        // HUD is not turbo-permanent; body swaps drop it. Re-attach anytime.
        if (!doc.body.contains(hud)) {
            doc.body.appendChild(hud);
        }
        if (!hud.contains(fpsLine) || !hud.contains(ftLine)) {
            hud.replaceChildren(fpsLine, ftLine);
        }
        fpsLine.textContent =
            fmtFps(lastFps) + " fps · ema " + fmtFps(fpsEma.ema);
        ftLine.textContent =
            fmtMs(lastFt) + " ms · ema " + fmtMs(ftEma.ema);
    }
    ensureHud();

    function draw(ms) {
        lastMs = ms;
        if (gl.isContextLost()) return;
        const secs = shaderSecs();
        const chromeTop = Math.max(0, win.outerHeight - win.innerHeight);
        const domX = (win.screenX || 0) * WORLD_SCALE;
        const domY = -((win.screenY || 0) + chromeTop + win.innerHeight) * WORLD_SCALE;
        const domScale = WORLD_SCALE * (win.innerHeight / Math.max(1, canH));
        const dt = lastDrawMs ? Math.min(0.1, (ms - lastDrawMs) / 1000) : 0;
        lastDrawMs = ms;
        tintCur += (tintTarget - tintCur) * (1.0 - Math.exp(-dt / 0.6));
        const wage = (ms - warpStart) / 1000;
        let w = 0;
        if (wage >= 0 && wage < 1.2) {
            const atk = 0.18;
            w = wage < atk ? wage / atk : 1.0 - (wage - atk) / (1.2 - atk);
            w = Math.max(0, w);
            w = w * w * (3 - 2 * w);
        }
        lastWarp = w;
        frameCount++;
        gl.viewport(0, 0, canW, canH);
        gl.useProgram(prog);
        const loc = gl.getAttribLocation(prog, "p");
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
        gl.uniform1f(u.t, secs);
        gl.uniform2f(u.r, canW, canH);
        gl.uniform2f(u.domainOffset, domX, domY);
        gl.uniform1f(u.domScale, domScale);
        gl.uniform1f(u.warp, w);
        gl.uniform1f(u.pageTint, tintCur);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function timedDraw(ms) {
        const t0 = performance.now();
        draw(ms);
        return performance.now() - t0;
    }

    function updateHud(ms, ftMs) {
        if (prevFrameMs > 0) {
            const frameDt = ms - prevFrameMs;
            if (frameDt > 0 && frameDt < 2000) {
                lastFps = 1000 / frameDt;
                pushEma(fpsEma, lastFps);
            }
        }
        prevFrameMs = ms;
        lastFt = ftMs;
        pushEma(ftEma, ftMs);
        fpsLine.textContent =
            fmtFps(lastFps) + " fps · ema " + fmtFps(fpsEma.ema);
        ftLine.textContent =
            fmtMs(lastFt) + " ms · ema " + fmtMs(ftEma.ema);
    }

    win.addEventListener("resize", () => {
        size();
        if (rm) {
            const ft = timedDraw(lastMs || performance.now());
            updateHud(performance.now(), ft);
        }
    });

    let rafId = 0, running = true;
    function step(ms) {
        if (!running) return;
        if (!doc.hidden) {
            const ft = timedDraw(ms);
            updateHud(ms, ft);
            rafId = win.requestAnimationFrame(step);
        } else {
            win.setTimeout(() => {
                if (running) rafId = win.requestAnimationFrame(step);
            }, 500);
        }
    }

    cv.addEventListener("webglcontextlost", (e) => {
        e.preventDefault();
        running = false;
        if (rafId) win.cancelAnimationFrame(rafId);
    });
    cv.addEventListener("webglcontextrestored", () => {
        prog = program(FS);
        if (!prog) return;
        size();
        running = true;
        if (rm) {
            const ft = timedDraw(lastMs || 0);
            updateHud(performance.now(), ft);
        } else {
            rafId = win.requestAnimationFrame(step);
        }
    });

    const handle = {
        canvas: cv,
        ensureHud,
        resume() {
            if (rm || running) return;
            running = true;
            rafId = win.requestAnimationFrame(step);
        },
        pulseWarp() {
            if (!rm) warpStart = lastMs || performance.now();
            tintTarget = tintTarget >= 0 ? -0.55 : 0.55;
        },
        get frames() {
            return frameCount;
        },
        get warp() {
            return lastWarp;
        },
        get fps() {
            return lastFps;
        },
        get fpsEma() {
            return fpsEma.ema;
        },
        get frametime() {
            return lastFt;
        },
        get frametimeEma() {
            return ftEma.ema;
        },
        stop() {
            running = false;
            if (rafId) win.cancelAnimationFrame(rafId);
            if (hud && hud.parentNode) hud.remove();
        },
    };

    if (rm) {
        const ft = timedDraw(0);
        updateHud(performance.now(), ft);
    } else {
        rafId = win.requestAnimationFrame(step);
    }
    return handle;
}

let handle = null;

export function initDreambg() {
    const cv = document.getElementById("sm-dreambg");
    if (!cv) return null;
    if (handle && handle.canvas === cv) {
        if (handle.ensureHud) handle.ensureHud();
        if (handle.resume) handle.resume();
        return handle;
    }
    if (handle) handle.stop();
    handle = mountDreambg(window, cv);
    window.smDreambg = handle;
    return handle;
}

export function pulseDreambg() {
    if (handle && handle.pulseWarp) handle.pulseWarp();
}

export default initDreambg;
